import {
  beingInstance,
  notEmpty,
  notUndefined,
  parseBoolean,
  parseJsonObjectAs,
  parseNumber,
  parseRecordAs,
  parseString,
  parsingArrayAs,
} from "@de-xima/xima-common-js-lang";

import { isFormThemeRootVolumeEntry } from "../client-form-theme-elfinder-helper.js";
import { deferredToPromise, runRemoteCommand } from "../../Util.ts";
import { createExecCommandCallback, decodeFileHash, encodeFileHash } from "../../elfinder/ElFinder.ts";

interface IThemeDeletionItem {
  detail: string;
  delete: boolean;
  themeId: number;
  themeName: string;
  themeUuid: string;
  volumeId: string;
}

interface IThemeGroup {
  header: string;
  items: IThemeDeletionItem[];
}

interface IFormThemeDeletionModel {
  deletable: IThemeGroup;
  deletableWithWarnings: IThemeGroup;
  nonDeletable: IThemeGroup;
}

function parseThemeDeletionItem(value: unknown): IThemeDeletionItem {
  return parseRecordAs(value, {
    detail: parseString,
    delete: parseBoolean,
    themeId: parseNumber,
    themeName: parseString,
    themeUuid: parseString,
    volumeId: parseString,
  });
}

function parseThemeGroup(value: unknown): IThemeGroup {
  return parseRecordAs(value, {
    header: parseString,
    items: parsingArrayAs(parseThemeDeletionItem),
  });
}

function parseFormThemeDeletionModel(value: unknown): IFormThemeDeletionModel | undefined {
  if (value === undefined) {
    return undefined;
  }
  return parseJsonObjectAs(value, {
    deletable: parseThemeGroup,
    deletableWithWarnings: parseThemeGroup,
    nonDeletable: parseThemeGroup,
  });
}

function getSelectedFormThemeData<T>(dialog: JQuery, dataExtractor: (el: HTMLElement) => T | undefined): T[] {
  return dialog
    .find(".form-theme-item")
    .toArray()
    .filter(beingInstance(HTMLInputElement))
    .filter((checkbox) => checkbox.checked)
    .map((element) => dataExtractor(element))
    .filter(notUndefined);
}

function getSelectedFormThemeHashes(dialog: JQuery): string[] {
  return getSelectedFormThemeData(dialog, (element) => element.dataset.themeVolumeId)
    .filter(notEmpty)
    .map((volumeId) => encodeFileHash(volumeId, ""));
}

function getSelectedFormThemeIds(dialog: JQuery): number[] {
  return getSelectedFormThemeData(dialog, (element) => element.dataset.themeId).map(parseNumber);
}

function renderThemeItem(item: IThemeDeletionItem, canDelete: boolean, showDetail: boolean): HTMLElement {
  const container = document.createElement("li");
  const text = showDetail ? item.detail : item.themeName;

  if (canDelete) {
    const widget = document.createElement("div");
    widget.classList.add("ui-selectbooleancheckbox", "ui-chkbox", "ui-widget");

    const helperHidden = document.createElement("div");
    helperHidden.classList.add("ui-helper-hidden-accessible");
    const hiddenInput = document.createElement("input");
    hiddenInput.id = `theme-${item.themeUuid}`;
    hiddenInput.dataset.themeId = item.themeId.toString();
    hiddenInput.dataset.themeUuid = item.themeUuid;
    hiddenInput.dataset.themeVolumeId = item.volumeId;
    hiddenInput.type = "checkbox";
    hiddenInput.checked = item.delete;
    hiddenInput.classList.add("form-theme-item");
    helperHidden.appendChild(hiddenInput);

    const checkbox = document.createElement("label");
    checkbox.classList.add("ui-chkbox-box", "ui-widget", "ui-corner-all", "ui-state-default");
    const checkboxIcon = document.createElement("span");
    checkboxIcon.classList.add("ui-chkbox-icon", "ui-icon", "ui-c");
    checkboxIcon.classList.add(item.delete ? "ui-icon-check" : "ui-icon-blank");
    checkbox.appendChild(checkboxIcon);
    checkbox.htmlFor = hiddenInput.id;

    const label = document.createElement("label");
    label.classList.add("ui-chkbox-label");
    label.textContent = text;
    label.htmlFor = hiddenInput.id;

    hiddenInput.addEventListener("change", () => {
      if (hiddenInput.checked) {
        checkboxIcon.classList.add("ui-icon-check");
        checkboxIcon.classList.remove("ui-icon-blank");
      } else {
        checkboxIcon.classList.remove("ui-icon-check");
        checkboxIcon.classList.add("ui-icon-blank");
      }
    });

    widget.appendChild(helperHidden);
    widget.appendChild(checkbox);
    widget.appendChild(label);

    container.appendChild(widget);
  } else {
    container.textContent = text;
  }

  return container;
}

function renderThemeGroup(group: IThemeGroup, type: string): HTMLElement {
  const container = document.createElement("div");
  container.classList.add(
    "client-form-theme-deletion-confirm__group",
    `client-form-theme-deletion-confirm__group--${type}`,
  );
  const header = document.createElement("p");
  header.textContent = group.header;
  container.appendChild(header);
  const list = document.createElement("ul");
  for (const item of group.items) {
    list.appendChild(renderThemeItem(item, type !== "non-deletable", type !== "deletable"));
  }
  container.appendChild(list);
  return container;
}

function renderDeleteDialogContent(deletionModel: IFormThemeDeletionModel): HTMLElement {
  const container = document.createElement("form");
  container.classList.add("client-form-theme-deletion-dialog-content");
  if (deletionModel.deletable.items.length > 0) {
    container.appendChild(renderThemeGroup(deletionModel.deletable, "deletable"));
  }
  if (deletionModel.deletableWithWarnings.items.length > 0) {
    container.appendChild(renderThemeGroup(deletionModel.deletableWithWarnings, "deletable-with-warnings"));
  }
  if (deletionModel.nonDeletable.items.length > 0) {
    container.appendChild(renderThemeGroup(deletionModel.nonDeletable, "non-deletable"));
  }
  return container;
}

async function deleteSelectedFormThemes(fm: elFinder.ElFinderInstance, dialog: JQuery): Promise<void> {
  try {
    const themeHashes = getSelectedFormThemeHashes(dialog);
    const themeIds = getSelectedFormThemeIds(dialog);
    if (themeHashes.length === 0 || themeIds.length === 0) {
      return;
    }
    const cwd = fm.cwd().hash ?? "";
    const root = fm.root(cwd, true);
    const deletesCwd = themeHashes.some((h) => cwd.startsWith(h));
    await runRemoteCommand(BackendCommand?.ClientFormTheme?.performFormThemeDeletion, [
      ...themeIds.map((id) => ({ name: "themeId", value: id }) as const),
    ]);
    fm.updateCache({ removed: themeHashes });
    fm.remove({ removed: themeHashes });
    if (deletesCwd) {
      await fm.exec("open", root);
    }
  } finally {
    try {
      await deferredToPromise(fm.sync());
    } catch (e) {
      console.error("Failed to sync after deleting form themes", e);
    }
    dialog.elfinderdialog("close");
  }
}

function countThemeDeletionItems(deletionModel: IFormThemeDeletionModel): number {
  return (
    deletionModel.deletable.items.length +
    deletionModel.deletableWithWarnings.items.length +
    deletionModel.nonDeletable.items.length
  );
}

async function deleteFormThemes(fm: elFinder.ElFinderInstance, hashes: string[]): Promise<void> {
  const response = await runRemoteCommand(BackendCommand?.ClientFormTheme?.requestFormThemeDeletion, [
    ...hashes
      .map((hash) => decodeFileHash(hash))
      .map((decoded) => decoded.volume)
      .map((volume) => ({ name: "volumeId", value: volume }) as const),
  ]);
  const deletionModel = parseFormThemeDeletionModel(response.deletionModel);
  if (deletionModel === undefined || countThemeDeletionItems(deletionModel) === 0) {
    return;
  }
  const dialogContent = renderDeleteDialogContent(deletionModel);
  return new Promise((resolve, reject) => {
    const dialog = fm.dialog(dialogContent, {
      modal: true,
      resizable: false,
      width: 800,
      cssClass: "client-form-theme-deletion-dialog",
      title: fm.i18n("deleteThemeDialogHeader"),
      close: () => {
        resolve();
        dialog.elfinderdialog("destroy");
      },
      buttons: {
        [fm.i18n("btnCancel")]: () => dialog.elfinderdialog("close"),
        [fm.i18n("deleteThemeDialogConfirm")]: () => deleteSelectedFormThemes(fm, dialog).then(resolve).catch(reject),
      },
    });
  });
}

/**
 * Creates a command for the ElFinder file manager that allows the user to delete the selected
 * form themes.
 *
 * This command is enabled when any number of form themes are selected.
 * @returns The new ElFinder command.
 */
export function createDeleteThemeCommand(): elFinder.ElFinderCommandHandlerBaseStatic<elFinder.BaseElFinderCommandOptions> {
  const command: elFinder.ElFinderCommandHandlerBaseStatic<elFinder.BaseElFinderCommandOptions> = function () {
    this.exec = createExecCommandCallback(deleteFormThemes);

    this.getstate = function (targets) {
      const files = this.files(targets);
      const allRoot = files.every((file) => isFormThemeRootVolumeEntry(file));
      return files.length > 0 && allRoot ? 1 : -1;
    };

    this.init = function (this: elFinder.ElFinderCommandHandlerBase<elFinder.BaseElFinderCommandOptions>) {
      this.className = "delete-theme";
      this.title = this.fm.i18n("deleteTheme");
      this.name = "deleteTheme";
    };
  };

  return command;
}
